/**
 * Composable for appointment-related utilities
 */

/**
 * Finds the next upcoming appointment for a patient from today onwards
 * @param {Array} appointments - Array of appointment objects with appointmentDate
 * @param {Number} patientId - Patient ID to filter appointments
 * @returns {Object|null} - The next appointment object or null if none found
 */
export function getNextAppointment(appointments, patientId) {
  if (!appointments || !patientId) return null;

  // Get today's date in YYYY-MM-DD format (no timezone issues)
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Filter appointments for this patient
  const patientAppointments = appointments.filter(apt => apt.patientId === patientId);
  
  if (patientAppointments.length === 0) return null;

  // Find future appointments (appointmentDate >= today) using string comparison
  const futureAppointments = patientAppointments.filter(apt => {
    if (!apt.appointmentDate) return false;
    
    // Compare as strings (YYYY-MM-DD format allows direct comparison)
    return apt.appointmentDate >= todayStr;
  });

  if (futureAppointments.length === 0) return null;

  // Sort by date ascending (earliest first) using string comparison
  const sortedAppointments = futureAppointments.sort((a, b) => {
    return a.appointmentDate.localeCompare(b.appointmentDate);
  });

  return sortedAppointments[0];
}

/**
 * Formats a Date object or date string to YYYY-MM-DD format
 * @param {Date|String} date - Date to format
 * @returns {String} - Formatted date string
 */
export function formatDateToISO(date) {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  const year = dateObj.getFullYear();
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Formats a Date object to datetime-local input format (YYYY-MM-DDTHH:MM)
 * @param {Date|String} date - Date to format
 * @returns {String} - Formatted datetime string
 */
export function formatDateTimeLocal(date) {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  const year = dateObj.getFullYear();
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getDate().toString().padStart(2, '0');
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}
